import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import CreateAssistantDialog from "./CreateAssistantDialog";
import { AssistantsList } from "./sections/AssistantsList";
import { usePlanRestrictions } from "@/hooks/usePlanRestrictions";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

const AIAssistantSettings = () => {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [assistantCount, setAssistantCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const { planLimits, isLoading: isPlanLoading } = usePlanRestrictions();

  // Fetch assistant count on component mount and refreshTrigger changes
  useEffect(() => {
    const fetchAssistantCount = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data, error, count } = await supabase
          .from('assistants')
          .select('*', { count: 'exact' });

        if (error) throw error;
        
        if (count !== null) {
          setAssistantCount(count);
          // Check if limit reached
          if (planLimits?.assistantsLimit !== null && 
              planLimits?.assistantsLimit !== undefined &&
              count >= planLimits.assistantsLimit) {
            setLimitReached(true);
          } else {
            setLimitReached(false);
          }
        }
      } catch (error) {
        console.error('Error fetching assistants count:', error);
        toast.error("Failed to fetch assistants count");
      }
    };

    fetchAssistantCount();
  }, [refreshTrigger, planLimits?.assistantsLimit]);

  const handleAssistantCreated = () => {
    console.log("Assistant created, triggering refresh");
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCloseDialog = () => {
    setShowCreateDialog(false);
  };

  // Helper function to display limit text
  const getLimitText = () => {
    if (isPlanLoading) return "Loading plan information...";
    
    if (planLimits?.assistantsLimit === null) return "Unlimited assistants allowed";
    
    return `${assistantCount} / ${planLimits?.assistantsLimit || 0} assistants used`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-4">MAIA Assistants</h2>
        <p className="text-sm text-muted-foreground mb-6">
          Create and manage your AI assistants.
        </p>
      </div>

      <div className="space-y-4">
        <AssistantsList 
          refreshTrigger={refreshTrigger}
          onCountChange={setAssistantCount}
        />

        <div className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">
            {getLimitText()}
          </p>
          
          <div className="flex justify-end pt-4">
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Assistant
                </Button>
              </DialogTrigger>
              {showCreateDialog && (
                limitReached ? (
                  <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Limit Reached</DialogTitle>
                      </DialogHeader>
                      <div className="py-6">
                        <p className="text-center text-muted-foreground">
                          You've reached the maximum number of assistants allowed in your current plan.
                          Please upgrade your account to create more assistants.
                        </p>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={handleCloseDialog}>
                          Close
                        </Button>
                        <Button onClick={() => window.location.href = "/settings?tab=tokens"}>
                          Upgrade Plan
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <CreateAssistantDialog 
                    onSave={handleAssistantCreated} 
                    onClose={handleCloseDialog}
                  />
                )
              )}
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantSettings;
